import { uniq } from 'lodash';
import memoize from 'lodash/memoize';
import uniqBy from 'lodash/uniqBy';
import { type TTypeTree, getSubTypes } from 'utils/objectTypes';
import {
  EOptionalRelation,
  type TAvailabilityRelationType,
  type TAvailabilityRelations,
  type TMemberType,
  type TMemberTypesItem,
  type TTEObject,
  type TType,
} from '@timeedit/types';

export type TObjectTypesMap = Record<number, TType>;

export const getSubTypeIds = (
  typeId: number,
  objectTypesMap: TObjectTypesMap
): number[] => {
  const type = objectTypesMap[typeId];

  if (!type) {
    return [];
  }

  return [
    typeId,
    ...(type.subTypes ?? []).flatMap((subTypeId) =>
      getSubTypeIds(subTypeId, objectTypesMap)
    ),
  ];
};

export const getMemberTypes = (
  objectTypes: number[],
  memberships: TMemberType[],
  objectTypesMap: TObjectTypesMap
): TType[] => {
  const memoizedGetSubTypeIds = memoize(
    (typeId: number) => getSubTypeIds(typeId, objectTypesMap),
    (typeId: number) => `${typeId}`
  );

  const memberTypes = objectTypes.flatMap((objectTypeId) => {
    return memberships
      .flatMap((membership) => {
        const memberTypeThatOverlapsWithObject = membership.memberTypes.find(
          (memberType: TMemberTypesItem) => {
            const allTypesOfMemberType = memoizedGetSubTypeIds(
              Number(memberType.type)
            );

            return allTypesOfMemberType.some(
              (typeId) => objectTypeId === typeId
            );
          }
        );

        if (!memberTypeThatOverlapsWithObject) {
          return null;
        }

        const memberTypesWithDifferentLevel = membership.memberTypes.filter(
          (memberType: TMemberTypesItem) =>
            memberType.memberLevel !==
            memberTypeThatOverlapsWithObject.memberLevel
        );

        return memberTypesWithDifferentLevel
          .filter((memberType) => {
            const memberTypeSubTypeIds = memoizedGetSubTypeIds(
              Number(memberType.type)
            );

            return !memberTypeSubTypeIds.includes(objectTypeId);
          })
          .flatMap((memberType) =>
            memoizedGetSubTypeIds(Number(memberType.type))
          )
          .filter(
            (typeId) => !memoizedGetSubTypeIds(objectTypeId).includes(typeId)
          )
          .map((typeId) => objectTypesMap[typeId]);
      })
      .filter(Boolean);
  });

  return uniqBy(memberTypes, (type) => type.id);
};

export const getAvailabilityRelatedTypes = (
  typeIds: number[],
  typeTree: TTypeTree,
  allAvailabilityRelations: TAvailabilityRelationType[],
  typeMap: Record<number, TType>
) => {
  const relatedTypes = new Set<number>();

  const targetingTypesWithParents = typeIds.flatMap((type) =>
    getSubTypes(type, typeTree)
  );

  const targetingTypesWithFullTree = typeIds.flatMap((type) => {
    const parentTypes = typeTree[type]?.ancestors || [];
    const subTypes = typeTree[type]?.descendants || [];
    return [type, ...parentTypes, ...subTypes];
  });

  allAvailabilityRelations.forEach(({ availabilityTypes = [] }) => {
    const typesInAvailabilityWithChildren = availabilityTypes.flatMap(
      ({ type }) => getSubTypes(Number(type), typeTree)
    );
    if (
      typesInAvailabilityWithChildren.some((type) =>
        targetingTypesWithParents.includes(type)
      )
    ) {
      typesInAvailabilityWithChildren.forEach((type) => {
        if (!targetingTypesWithFullTree.includes(type)) {
          relatedTypes.add(type);
        }
      });
    }
  });

  return Array.from(relatedTypes)
    .map((type) => typeMap[type])
    .filter((type) => !!type);
};

export const getAvailabilityRelationChange = (
  addedObjects: TTEObject[],
  availabilityRelations: TAvailabilityRelations[],
  allAvailabilityRelations: TAvailabilityRelationType[],
  typeTree: TTypeTree,
  types: number[],
  onChange: (object: Partial<TTEObject>) => void
) => {
  const newAvailabilityRelations = addedObjects
    .filter((addedObject) => {
      return !availabilityRelations.some((availRel) =>
        availRel.objectIds.includes(addedObject.id)
      );
    })
    .map((addedObject) => {
      if (!addedObject) {
        return;
      }
      const availabilityType = allAvailabilityRelations.find(
        ({ availabilityTypes = [] }) => {
          const addedTypesWithParents = addedObject.types.flatMap((type) => {
            const parentTypes = typeTree[type]?.ancestors || [];
            return [type, ...parentTypes];
          });

          const targetingTypesWithParents = types.flatMap((type) => {
            const parentTypes = typeTree[type]?.ancestors || [];
            return [type, ...parentTypes];
          });

          return (
            availabilityTypes.some((t) =>
              addedTypesWithParents.includes(Number(t.type))
            ) &&
            availabilityTypes.some((t) =>
              targetingTypesWithParents.includes(Number(t.type))
            )
          );
        }
      );
      if (!availabilityType) {
        return;
      }
      return {
        objectIds: [addedObject.id],
        availabilityTypeId: availabilityType.id,
      };
    })
    .filter((addedRelation) => addedRelation != null);

  onChange({
    availabilityRelations: [
      ...availabilityRelations,
      ...newAvailabilityRelations,
    ],
  });
};

export const getOptionalRelatedTypes = (
  types: number[],
  objectTypesMap: Record<number, TType>,
  typeTree: TTypeTree,
  reservationTemplatesMap: Record<number, any>
) => {
  const ancestors = types.flatMap((type) => typeTree?.[type]?.ancestors ?? []);

  // Get all reservation templates that match the current object types and their ancestors
  const matchingReservationTemplates = uniq([...types, ...ancestors])
    .flatMap((type) => objectTypesMap[type]?.reservationTemplates ?? [])
    .map((templateId) => reservationTemplatesMap?.[templateId])
    .filter(Boolean);

  // Check the reservation templates for types with optional relations
  const typesWithOptionalRelated = matchingReservationTemplates
    .filter((template) => {
      return template.types.some(
        (t) => t.optionalRelation === EOptionalRelation.OPTIONAL
      );
    })
    .flatMap((template) => template.types)
    .map((templateType) => templateType.type);

  return typesWithOptionalRelated;
};
